import { useLayoutEffect, useMemo, useRef } from 'react';
import { useCoreApi } from 'infrastructure/api';
import type {
  AllocationSummaryDto,
  ChildrenPageDto,
  CoreEventDto,
  InventoryDto,
  ListResponse,
  ObligationListDto,
  PersonDto,
  SyncStatusDto,
  TreeOverviewDto,
} from '../types';

type QueryValue = string | number | boolean | null | undefined;

type CoreServiceTransport = Pick<ReturnType<typeof useCoreApi>, 'sendGetRequest'>;

export type TreeChildrenParams = {
  openOnly?: boolean;
  q?: string;
  state?: string;
  tracker?: string;
  service?: string;
  project?: string;
  flag?: string;
  origin?: string;
  process?: string;
  run?: string;
  initiator?: string;
  view?: string;
  sort?: string;
  pageNumber?: number;
  pageSize?: number;
};

const buildQuery = (params: Record<string, QueryValue>): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();

  return query ? `?${query}` : '';
};

const readJson = async <T>(response: unknown): Promise<T> => {
  const reader = response as { json?: () => Promise<T> };

  return typeof reader?.json === 'function' ? reader.json() : (response as T);
};

const readText = async (response: unknown): Promise<string> => {
  const reader = response as { text?: () => Promise<string> };

  return typeof reader?.text === 'function' ? reader.text() : String(response ?? '');
};

const createCoreServiceClient = (transport: CoreServiceTransport) => {
  const getJson = async <T>(path: string): Promise<T> => readJson<T>(await transport.sendGetRequest(path));
  const getText = async (path: string): Promise<string> => readText(await transport.sendGetRequest(path));

  return {
    getSyncStatus: () => getJson<SyncStatusDto>('/sync/status'),

    listPeople: () => getJson<ListResponse<PersonDto>>(`/people${buildQuery({ pageSize: 500 })}`),

    getObligations: (loginOrId: string, includeSteady: boolean) =>
      getJson<ObligationListDto>(
        `/people/${encodeURIComponent(loginOrId)}/obligations${buildQuery({ includeSteady })}`,
      ),

    getInventory: () => getJson<InventoryDto>('/blueprints/inventory'),
    getDefinitionYaml: (definitionId: string) =>
      getText(`/blueprints/definitions/${encodeURIComponent(definitionId)}/yaml`),
    getDefinitionsYaml: () => getText('/blueprints/export.yaml'),
    getDefinitionSchema: () => getText('/blueprints/schema'),

    getTreeOverview: () => getJson<TreeOverviewDto>('/tree/overview'),
    getTreeChildren: (nodeId: string, params: TreeChildrenParams) =>
      getJson<ChildrenPageDto>(`/tree/nodes/${encodeURIComponent(nodeId)}/children${buildQuery(params)}`),

    getAllocationSummary: () => getJson<AllocationSummaryDto>('/cost-allocation/summary'),

    getRecentEvents: (limit: number) => getJson<CoreEventDto[]>(`/events${buildQuery({ limit })}`),
    getEventsAfter: (after: number, limit: number) => getJson<CoreEventDto[]>(`/events${buildQuery({ after, limit })}`),
  };
};

type CoreServiceClient = ReturnType<typeof createCoreServiceClient>;

/** A client with a stable identity: the underlying api object is recreated on every render of the host component. */
export const useCoreServiceClient = (): CoreServiceClient => {
  const api = useCoreApi();
  const apiRef = useRef(api);

  useLayoutEffect(() => {
    apiRef.current = api;
  }, [api]);

  return useMemo(
    () =>
      createCoreServiceClient({
        sendGetRequest: (...args: Parameters<CoreServiceTransport['sendGetRequest']>) =>
          apiRef.current.sendGetRequest(...args),
      }),
    [],
  );
};
